"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface RegistrationDetailsFormProps {
  onNext: (data: RegistrationDetailsData) => void;
  onBack: () => void;
}

export interface RegistrationDetailsData {
  registrationNumber: string;
  registrationDate: string;
  expiryDate: string;
  issuingAuthority: string;
  registrationState: string;
}

export default function RegistrationDetailsForm({ onNext, onBack }: RegistrationDetailsFormProps) {
  const [formData, setFormData] = useState<RegistrationDetailsData>({
    registrationNumber: "",
    registrationDate: "",
    expiryDate: "",
    issuingAuthority: "",
    registrationState: "",
  });

  const [errors, setErrors] = useState<Partial<RegistrationDetailsData>>({});

  const norwegianCounties = [
    "Agder", "Innlandet", "Møre og Romsdal", "Nordland", "Oslo", "Rogaland",
    "Troms og Finnmark", "Trøndelag", "Vestfold og Telemark", "Vestland", "Viken"
  ];

  const handleChange = (field: keyof RegistrationDetailsData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<RegistrationDetailsData> = {};

    if (!formData.registrationNumber) newErrors.registrationNumber = "Registreringsnummer er påkrevd";
    if (!formData.registrationDate) newErrors.registrationDate = "Registreringsdato er påkrevd";
    if (!formData.expiryDate) newErrors.expiryDate = "Utløpsdato er påkrevd";
    if (!formData.issuingAuthority) newErrors.issuingAuthority = "Utstedende myndighet er påkrevd";
    if (!formData.registrationState) newErrors.registrationState = "Fylke er påkrevd";

    if (formData.registrationDate && formData.expiryDate) {
      const regDate = new Date(formData.registrationDate);
      const expDate = new Date(formData.expiryDate);
      if (expDate <= regDate) {
        newErrors.expiryDate = "Utløpsdato må være etter registreringsdato";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-bold text-foreground mb-2">
          Registreringsnummer *
        </label>
        <input
          type="text"
          value={formData.registrationNumber}
          onChange={(e) => handleChange("registrationNumber", e.target.value.toUpperCase())}
          placeholder="f.eks. REG-2024-12345"
          className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            errors.registrationNumber ? "border-error" : "border-neutral-200 hover:border-neutral-300"
          }`}
        />
        {errors.registrationNumber && (
          <p className="mt-2 text-sm text-error font-medium">{errors.registrationNumber}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-bold text-foreground mb-2">
            Registreringsdato *
          </label>
          <input
            type="date"
            value={formData.registrationDate}
            onChange={(e) => handleChange("registrationDate", e.target.value)}
            max={new Date().toISOString().split("T")[0]}
            className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              errors.registrationDate ? "border-error" : "border-neutral-200 hover:border-neutral-300"
            }`}
          />
          {errors.registrationDate && (
            <p className="mt-2 text-sm text-error font-medium">{errors.registrationDate}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-bold text-foreground mb-2">
            Utløpsdato *
          </label>
          <input
            type="date"
            value={formData.expiryDate}
            onChange={(e) => handleChange("expiryDate", e.target.value)}
            min={formData.registrationDate || new Date().toISOString().split("T")[0]}
            className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              errors.expiryDate ? "border-error" : "border-neutral-200 hover:border-neutral-300"
            }`}
          />
          {errors.expiryDate && (
            <p className="mt-2 text-sm text-error font-medium">{errors.expiryDate}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold text-foreground mb-2">
          Utstedende myndighet *
        </label>
        <input
          type="text"
          value={formData.issuingAuthority}
          onChange={(e) => handleChange("issuingAuthority", e.target.value)}
          placeholder="f.eks. Statens vegvesen"
          className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            errors.issuingAuthority ? "border-error" : "border-neutral-200 hover:border-neutral-300"
          }`}
        />
        {errors.issuingAuthority && (
          <p className="mt-2 text-sm text-error font-medium">{errors.issuingAuthority}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-bold text-foreground mb-2">
          Fylke *
        </label>
        <select
          value={formData.registrationState}
          onChange={(e) => handleChange("registrationState", e.target.value)}
          className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            errors.registrationState ? "border-error" : "border-neutral-200 hover:border-neutral-300"
          }`}
        >
          <option value="">Velg et fylke</option>
          {norwegianCounties.map((county) => (
            <option key={county} value={county}>
              {county}
            </option>
          ))}
        </select>
        {errors.registrationState && (
          <p className="mt-2 text-sm text-error font-medium">{errors.registrationState}</p>
        )}
      </div>

      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          className="px-8 py-3 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-all flex items-center gap-2"
        >
          <Icon name="ArrowLeftIcon" size={18} />
          Tilbake
        </button>
        <button
          type="submit"
          className="px-8 py-3 bg-blue-800 text-white rounded-lg font-semibold hover:bg-blue-900 transition-all flex items-center gap-2"
        >
          Fortsett til eierdetaljer
          <Icon name="ArrowRightIcon" size={18} />
        </button>
      </div>
    </form>
  );
}